import { useElementSize } from '@mantine/hooks';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { businessToday, type ClientStage } from '@mexp/shared';
import { addDays, dayMonth, daysUntil, stageLabel, STAGE_COLOR } from './stage';

export interface TimelineItem {
  id: string;
  /** What the request is for, e.g. the product name. Truncated first when the chip runs out of room. */
  title: string;
  /** Where it is going, e.g. "Kenya". Always shown in full. */
  place: string;
  /** Full description for screen readers, e.g. "DR-0012, Persea capsules for Kenya". */
  detail: string;
  requiredBy: string;
  stage: ClientStage;
}

const LANE = 36;
const CHIP_H = 26;
const STEM_MIN = 12;
const MAX_LANES = 6;
const CHIP_GAP = 8;
/** Widest a chip grows before its title truncates: roomier where the canvas is wide. */
const chipMax = (canvasWidth: number) => (canvasWidth >= 900 ? 240 : 190);
const MIN_TITLE = 56;
/** Chip chrome around the two texts: padding, dot, gaps and border. */
const CHIP_CHROME = 38;

let measurer: CanvasRenderingContext2D | null | undefined;
/** Width of chip text in the chip's own font, so layout and truncation agree. */
function textWidth(text: string): number {
  if (measurer === undefined) {
    measurer = document.createElement('canvas').getContext('2d');
    if (measurer) measurer.font = `12px ${getComputedStyle(document.body).fontFamily}`;
  }
  return measurer ? measurer.measureText(text).width : text.length * 6.6;
}

function fullWidth(item: TimelineItem, max: number): number {
  return Math.min(max, Math.ceil(CHIP_CHROME + textWidth(item.title) + textWidth(`· ${item.place}`)));
}

/** The narrowest a chip may get: the place in full and the start of the title. */
function minWidth(item: TimelineItem, max: number): number {
  return Math.min(fullWidth(item, max), Math.ceil(CHIP_CHROME + textWidth(`· ${item.place}`) + MIN_TITLE));
}

/** The draw-in plays once per page load for each rendition, so returning to Home does not replay it. */
const played = new Set<'example' | 'working'>();

interface Chip {
  item: TimelineItem;
  left: number;
  px: number;
  start: number;
  end: number;
  lane: number;
  flip: boolean;
}

const byDate = (a: TimelineItem, b: TimelineItem) =>
  a.requiredBy < b.requiredBy ? -1 : a.requiredBy > b.requiredBy ? 1 : a.id.localeCompare(b.id);

/**
 * Lanes, lowest first, filled right to left. Each chip hangs to the right of its pin, so a chip that
 * would cover the pin of a chip already placed either stops short of it or climbs above it, and no
 * stem passes behind another chip. Chips that share a date share a pin and simply stack. Returned in
 * date order, which is also the keyboard order.
 */
function placeChips(items: TimelineItem[], x: (date: string) => number, canvasWidth: number): Chip[] {
  const sorted = [...items].sort((a, b) => byDate(b, a));
  const pins = sorted.map((item) => (x(item.requiredBy) / 100) * canvasWidth);
  // Narrow canvases favour fewer lanes; wide ones favour whole titles.
  const laneCost = canvasWidth < 900 ? 0.4 : 0.2;
  const chips: Chip[] = [];
  sorted.forEach((item, idx) => {
    const left = x(item.requiredBy);
    const px = pins[idx] as number;
    const full = fullWidth(item, chipMax(canvasWidth));
    const min = minWidth(item, chipMax(canvasWidth));
    const spans: { start: number; end: number; flip: boolean }[] = [];
    const end = Math.min(px - 10 + full, canvasWidth - 2);
    if (end - (px - 10) >= min) spans.push({ start: px - 10, end, flip: false });
    const nextPin = Math.min(...chips.filter((c) => c.px > px + 0.5 && c.px - 10 - CHIP_GAP < end).map((c) => c.px));
    if (Number.isFinite(nextPin) && nextPin - 10 - CHIP_GAP - (px - 10) >= min) {
      spans.push({ start: px - 10, end: nextPin - 10 - CHIP_GAP, flip: false });
    }
    // Only a chip with no room on the right hangs to the left of its pin.
    if (!spans.length) spans.push({ start: Math.max(px + 10 - full, 2), end: px + 10, flip: true });

    let best: Chip | null = null;
    let bestCost = Number.POSITIVE_INFINITY;
    for (let lane = 0; lane < MAX_LANES; lane++) {
      for (const span of spans) {
        const w = span.end - span.start;
        let cost = lane * laneCost + (w < full - 0.5 ? 0.3 + 1.5 * (1 - w / full) : 0) + (span.flip ? 0.1 : 0);
        for (const c of chips) {
          if (c.lane === lane) {
            if (span.start < c.end + CHIP_GAP && span.end > c.start - CHIP_GAP) cost += 100;
          } else if (Math.abs(c.px - px) > 0.5) {
            // Our stem behind a lower chip, or our chip over a higher chip's stem.
            if (c.lane < lane && px >= c.start - 3 && px <= c.end + 3) cost += 10;
            if (c.lane > lane && c.px >= span.start - 3 && c.px <= span.end + 3) cost += 10;
          }
        }
        // Pins still to be placed (to the left) that this chip would sit over.
        for (let j = idx + 1; j < pins.length; j++) {
          const p = pins[j] as number;
          if (Math.abs(p - px) > 0.5 && p >= span.start - 3 && p <= span.end + 3) cost += 10;
        }
        if (cost < bestCost) {
          bestCost = cost;
          best = { item, left, px, start: span.start, end: span.end, lane, flip: span.flip };
        }
      }
    }
    chips.push(best as Chip);
  });
  return chips.sort((a, b) => byDate(a.item, b.item));
}

/**
 * The portal's signature view: every open request placed on its needed-by date, today marked,
 * overdue requests in the tinted band on the left. Chips stack in lanes so none overlap.
 */
export function NeededByTimeline({
  items,
  activeId = null,
  onActivate,
  onSelect,
  inverse = false,
  weeksAhead = 8,
  today = businessToday(),
  label = 'Needed-by timeline',
}: {
  items: TimelineItem[];
  activeId?: string | null;
  onActivate?: (id: string | null) => void;
  onSelect?: (id: string) => void;
  inverse?: boolean;
  weeksAhead?: number;
  today?: string;
  label?: string;
}) {
  const { ref, width } = useElementSize();
  const interactive = !!onSelect;
  // Lay out again once the web font is in, since chip widths are measured in it.
  const [, setFontsReady] = useState(false);
  useEffect(() => {
    let live = true;
    void document.fonts?.ready.then(() => {
      measurer = undefined;
      if (live) setFontsReady(true);
    });
    return () => {
      live = false;
    };
  }, []);
  const rendition = inverse ? 'example' : 'working';
  const [animate] = useState(() => !played.has(rendition));
  useEffect(() => {
    played.add(rendition);
  }, [rendition]);

  // On phones the working timeline scrolls sideways; fade whichever edge has more to see.
  const scroller = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });
  const updateFade = useCallback(() => {
    const el = scroller.current;
    if (!el || inverse) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setFade((f) => (f.left === left && f.right === right ? f : { left, right }));
  }, [inverse]);
  useEffect(updateFade, [updateFade, width]);

  // The illustration fits its box; the working timeline keeps a readable minimum and scrolls on phones.
  const canvasWidth = inverse ? Math.max(width, 1) : Math.max(width, 640);
  const dates = items.map((i) => i.requiredBy).sort();
  const earliest = dates[0] ?? today;
  const latest = dates[dates.length - 1] ?? today;
  const floor = addDays(today, -28);
  const start = [addDays(today, -7), addDays(earliest, -3)].sort()[0] as string;
  const from = start < floor ? floor : start;
  // Leave the latest chip room to hang to the right of its pin.
  const reach = chipMax(canvasWidth) - 10;
  const lead = Math.ceil((reach * Math.max(daysUntil(latest, from), 1)) / Math.max(canvasWidth - reach, 120));
  const wanted = [addDays(today, weeksAhead * 7), addDays(latest, Math.max(7, lead))].sort()[1] as string;
  const cap = addDays(today, 182);
  const to = wanted > cap ? cap : wanted;
  const span = Math.max(daysUntil(to, from), 1);
  const x = (date: string) => Math.min(Math.max(daysUntil(date, from) / span, 0), 1) * 100;

  // Mondays between the ends, thinned so labels never collide.
  const mondays: string[] = [];
  const firstMonday = addDays(from, (8 - new Date(`${from}T00:00:00Z`).getUTCDay()) % 7);
  for (let d = firstMonday; d <= to; d = addDays(d, 7)) mondays.push(d);
  const step = Math.max(1, Math.ceil(mondays.length / Math.max(Math.floor(canvasWidth / 74), 1)));
  // A tick keeps its label only when the whole label fits inside the canvas.
  const ticks = mondays
    .filter((_, i) => i % step === 0)
    .map((d) => {
      const px = (x(d) / 100) * canvasWidth;
      const half = (textWidth(dayMonth(d)) * 11) / 24 + 2;
      return { d, labelled: px - half >= 0 && px + half <= canvasWidth };
    });

  const chips = placeChips(items, x, canvasWidth);
  const lanes = Math.max(...chips.map((c) => c.lane + 1), 1);
  const axisY = 10 + lanes * LANE + STEM_MIN;
  const height = axisY + 44;
  const todayX = x(today);

  return (
    <div
      ref={scroller}
      onScroll={updateFade}
      className={`nbt${inverse ? ' nbt-inverse' : ''}`}
      data-animate={animate || undefined}
      data-fade-left={fade.left || undefined}
      data-fade-right={fade.right || undefined}
      aria-hidden={interactive ? undefined : true}
    >
      <div ref={ref} className="nbt-canvas" style={{ height }} role={interactive ? 'group' : undefined} aria-label={interactive ? label : undefined}>
        {todayX > 0 && <div className="nbt-past" style={{ left: 0, width: `${todayX}%`, height: axisY + 6 }} />}
        {ticks.map(({ d, labelled }) => (
          <div key={d}>
            <div className="nbt-tick" style={{ left: `${x(d)}%`, top: 4, height: axisY - 4 }} />
            {labelled && (
              <div className="nbt-tick-label" style={{ left: `${x(d)}%`, top: axisY + 14 }}>
                {dayMonth(d)}
              </div>
            )}
          </div>
        ))}
        <div className="nbt-axis" style={{ top: axisY - 1 }} />
        <div className="nbt-today" style={{ left: `${todayX}%`, height: axisY + 6 }} />
        <div className="nbt-today-label" style={{ left: `${todayX}%`, top: axisY + 26 }}>
          Today
        </div>
        {chips.map(({ item, left, lane, flip, px, start: chipStart, end: chipEnd }, i) => {
          // Chips that share a date share a pin: a stacked chip's stem stops at the chip below it instead of crossing it.
          const below = Math.max(-1, ...chips.filter((c) => c.lane < lane && Math.abs(c.px - px) <= 0.5).map((c) => c.lane));
          const stem = below >= 0 ? (lane - below) * LANE - CHIP_H : STEM_MIN + lane * LANE;
          const color = STAGE_COLOR[item.stage];
          const dot = `var(--mantine-color-${color}-${inverse ? 3 : color === 'gray' ? 5 : 6})`;
          const top = axisY - STEM_MIN - CHIP_H - lane * LANE;
          const style = {
            left: `${left}%`,
            top,
            maxWidth: chipEnd - chipStart,
            '--i': i,
            '--z': lane + 1,
            '--stem': `${stem}px`,
            '--dot': dot,
            '--tx': flip ? 'calc(-100% + 10px)' : '-10px',
          } as CSSProperties;
          const content = (
            <>
              <span className="nbt-marker-dot" />
              <span className="nbt-marker-title">{item.title}</span>
              <span className="nbt-marker-place">· {item.place}</span>
            </>
          );
          return (
            <div key={item.id}>
              <span
                className="nbt-pin"
                style={
                  {
                    left: `${left}%`,
                    top: axisY - 4.5,
                    '--dot': dot,
                  } as CSSProperties
                }
              />
              {interactive ? (
                <button
                  type="button"
                  className="nbt-marker"
                  data-flip={flip || undefined}
                  data-active={activeId === item.id || undefined}
                  style={style}
                  aria-label={`${item.detail}. Needed by ${dayMonth(item.requiredBy)}. ${stageLabel(item.stage)}.`}
                  onMouseEnter={() => onActivate?.(item.id)}
                  onMouseLeave={() => onActivate?.(null)}
                  onFocus={() => onActivate?.(item.id)}
                  onBlur={() => onActivate?.(null)}
                  onClick={() => onSelect?.(item.id)}
                >
                  {content}
                </button>
              ) : (
                <div className="nbt-marker" data-static data-flip={flip || undefined} style={style}>
                  {content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
